// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2025-2026 Daragh Farrell

import { ReactNode, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { getAdminToken, clearAdminToken } from '../api/adminClient';

type Props = {
  children: ReactNode;
};

const NAV_LINKS = [
  { path: '/admin/clubs', label: 'Clubs' },
  { path: '/admin/revenue', label: 'Revenue' },
  { path: '/admin/commission', label: 'Commission' },
  { path: '/admin/payouts', label: 'Payouts' },
  { path: '/admin/versions', label: 'Versions' },
];

export default function AdminLayout({ children }: Props) {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!getAdminToken()) {
      navigate('/admin/login', { replace: true });
    }
  }, [navigate]);

  if (!getAdminToken()) return null;

  const handleLogout = () => {
    clearAdminToken();
    navigate('/admin/login', { replace: true });
  };

  const showAlphaBanner = import.meta.env.VITE_ALPHA_BANNER !== 'false';

  return (
    <div className="min-h-screen bg-slate-50">
      {showAlphaBanner && (
        <div className="bg-amber-50 border-b border-amber-200 px-4 py-2 text-center text-sm text-amber-800">
          <strong>Alpha</strong> — This is an early preview. Features may change or break. Your feedback helps us build something great.
        </div>
      )}
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3">
          <span className="text-lg font-semibold text-brand-700">ClubView Admin</span>
          <nav className="flex items-center gap-1">
            {NAV_LINKS.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`rounded-lg px-3 py-1.5 text-sm font-medium transition ${
                  location.pathname === link.path
                    ? 'bg-brand-50 text-brand-700'
                    : 'text-slate-600 hover:bg-slate-100'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>
          <button
            onClick={handleLogout}
            className="rounded-lg px-3 py-1.5 text-sm font-medium text-slate-600 hover:bg-slate-100"
          >
            Log out
          </button>
        </div>
      </header>
      <main className="mx-auto max-w-7xl px-4 py-6">{children}</main>
    </div>
  );
}
